import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGS = [
  { code: "es", label: "ES", flag: "🇪🇸", name: "Español" },
  { code: "en", label: "EN", flag: "🇬🇧", name: "English" },
  { code: "fr", label: "FR", flag: "🇫🇷", name: "Français" },
] as const;

interface Props {
  variant?: "compact" | "full";
  className?: string;
}

const LanguageSwitcher = ({ variant = "compact", className = "" }: Props) => {
  const { i18n, t } = useTranslation();
  const currentCode = i18n.language?.split("-")[0] ?? "es";
  const current = LANGS.find((l) => l.code === currentCode) ?? LANGS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`${t("lang.label")}: ${current.name}`}
        title={current.name}
        className={`inline-flex items-center justify-center gap-1.5 h-9 min-w-[68px] px-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground-strong hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
      >
        <Globe className="w-4 h-4" aria-hidden="true" />
        <span aria-hidden="true">{current.label}</span>
        <span className="sr-only">{current.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <DropdownMenuRadioGroup
          value={currentCode}
          onValueChange={(code) => i18n.changeLanguage(code)}
        >
          {LANGS.map((l) => (
            <DropdownMenuRadioItem
              key={l.code}
              value={l.code}
              className={currentCode === l.code ? "font-semibold text-primary" : ""}
            >
              <span className="mr-2" aria-hidden="true">{l.flag}</span>
              {variant === "full" ? l.name : l.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
